import CryptoJS from 'crypto-js';

/**
 * Verifica si los datos parecen estar encriptados con nuestro formato
 * @param {string} data - Datos a verificar
 * @returns {boolean} - True si parece estar encriptado
 */
export function isLikelyEncrypted(data) {
  if (typeof data !== 'string') return false;
  
  // Patrones típicos de datos encriptados con CryptoJS
  const encryptedPatterns = [
    /^U2FsdGVkX1/, // CryptoJS AES con salt (común)
    /^[\w+/]+={0,2}$/, // Base64 válido
    /\$/, // CryptoJS usa $ en su formato
  ];

  return encryptedPatterns.some(pattern => pattern.test(data));
}

/**
 * Encrypts data using AES encryption with a password.
 * @param {string} data - The data to encrypt (e.g., JSON string of wallet data).
 * @param {string} password - The password to use for encryption.
 * @returns {Promise<string>} - The encrypted data as a base64 string.
 * @throws {Error} - If encryption fails or inputs are invalid.
 */
export async function encryptData(data, password) {
  try {
    // Validate inputs
    if (!data || typeof data !== 'string') {
      throw new Error('Datos inválidos: debe ser una cadena no vacía');
    }
    if (!password || typeof password !== 'string') {
      throw new Error('Contraseña inválida: debe ser una cadena no vacía');
    }

    // Encrypt using AES
    const encrypted = CryptoJS.AES.encrypt(data, password).toString();
    
    // Log for debugging (opcional en producción)
    if (process.env.NODE_ENV !== 'production') {
      console.debug('DEBUG: encryptData:', {
        inputLength: data.length,
        encryptedPrefix: encrypted.substring(0, 10),
      });
    }

    return encrypted;
  } catch (error) {
    console.error('Error en encryptData:', error.message);
    throw new Error('Error al encriptar los datos: ' + error.message);
  }
}

/**
 * Decrypts data using AES decryption with a password.
 * @param {string} encryptedData - The encrypted data (base64 string).
 * @param {string} password - The password used for decryption.
 * @returns {Promise<string>} - The decrypted data as a string.
 * @throws {Error} - If decryption fails or inputs are invalid.
 */
export async function decryptData(encryptedData, password) {
  try {
    // Validate inputs
    if (!encryptedData || typeof encryptedData !== 'string') {
      throw new Error('Datos encriptados inválidos: debe ser una cadena no vacía');
    }
    if (!password || typeof password !== 'string') {
      throw new Error('Contraseña inválida: debe ser una cadena no vacía');
    }

    // Attempt decryption
    const bytes = CryptoJS.AES.decrypt(encryptedData, password);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);

    // Check if decryption was successful
    if (!decrypted) {
      throw new Error('Falló la desencriptación (contraseña incorrecta o datos corruptos)');
    }

    return decrypted;
  } catch (error) {
    console.error('Error en decryptData:', {
      error: error.message,
      inputLength: encryptedData?.length || 0,
    });
    throw new Error('Error al desencriptar los datos: ' + error.message);
  }
}